interface LogLevel {
  ERROR: 'error';
  WARN: 'warn';
  INFO: 'info';
  DEBUG: 'debug';
}

interface LogEntry {
  timestamp: string;
  level: keyof LogLevel;
  category: string;
  message: string;
  data?: any;
  userId?: string;
  sessionId: string;
}

class Logger {
  private sessionId: string;
  private userId?: string;
  private isDevelopment: boolean;
  private logBuffer: LogEntry[] = [];
  private maxBufferSize = 100;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    // In development, enable all logs
    // In production, only log important events
    if (this.isDevelopment) {
      console.log('🚀 ThreatCompass Logger initialized', { sessionId: this.sessionId });
    }
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  setUserId(userId: string) {
    if (this.userId === userId) {
      return; // Prevent duplicate user ID setting
    }
    this.userId = userId;
    this.info('AUTH', 'User logged in', { userId });
  }

  clearUserId() {
    this.info('AUTH', 'User logged out', { userId: this.userId });
    this.userId = undefined;
  }

  private createLogEntry(level: keyof LogLevel, category: string, message: string, data?: any): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      userId: this.userId,
      sessionId: this.sessionId,
    };
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry);
    
    // Keep buffer size manageable
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
    }
  }

  private shouldLog(level: keyof LogLevel): boolean {
    if (this.isDevelopment) return true;
    
    // In production, only log warnings and errors
    return level === 'ERROR' || level === 'WARN';
  }

  error(category: string, message: string, data?: any) {
    const entry = this.createLogEntry('ERROR', category, message, data);
    this.addToBuffer(entry);
    
    console.error(`[${category}] ${message}`, data);
    
    // In production, could send to error tracking service
    if (!this.isDevelopment) {
      this.sendToRemoteLogger(entry);
    }
  }

  warn(category: string, message: string, data?: any) {
    const entry = this.createLogEntry('WARN', category, message, data);
    this.addToBuffer(entry);
    
    if (this.shouldLog('WARN')) {
      console.warn(`[${category}] ${message}`, data);
    }
  }

  info(category: string, message: string, data?: any) {
    const entry = this.createLogEntry('INFO', category, message, data);
    this.addToBuffer(entry);
    
    if (this.shouldLog('INFO')) {
      console.info(`[${category}] ${message}`, data);
    }
  }

  debug(category: string, message: string, data?: any) {
    const entry = this.createLogEntry('DEBUG', category, message, data);
    this.addToBuffer(entry);
    
    if (this.shouldLog('DEBUG')) {
      console.debug(`[${category}] ${message}`, data);
    }
  }

  // Specific logging methods for common scenarios
  apiRequest(method: string, url: string, data?: any) {
    this.debug('API', `${method} ${url}`, data);
  }

  apiResponse(method: string, url: string, status: number, duration?: number) {
    const message = `${method} ${url} → ${status}`;
    const data = { 
      status, 
      duration: duration ? `${duration}ms` : undefined 
    };
    
    if (status >= 400) {
      this.warn('API', message, data);
    } else {
      this.debug('API', message, data);
    }
  }

  apiError(method: string, url: string, error: any) {
    this.error('API', `${method} ${url} failed`, {
      error: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
  }

  userAction(action: string, data?: any) {
    this.info('USER', action, data);
  }

  navigation(from: string, to: string) {
    this.debug('NAVIGATION', `${from} → ${to}`);
  }

  authentication(event: 'login_attempt' | 'login_success' | 'login_failure' | 'logout', data?: any) {
    this.info('AUTH', event, data);
  }

  // Get recent logs for debugging
  getRecentLogs(count = 50): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  // Export logs for debugging
  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }

  // Send logs to remote service (placeholder)
  private async sendToRemoteLogger(entry: LogEntry) {
    // This would integrate with services like Sentry, LogRocket, etc.
    // For now, just store in local storage for debugging
    try {
      const existingLogs = localStorage.getItem('tc_error_logs');
      const logs = existingLogs ? JSON.parse(existingLogs) : [];
      logs.push(entry);
      
      // Keep only last 20 error logs in localStorage
      if (logs.length > 20) {
        logs.splice(0, logs.length - 20);
      }
      
      localStorage.setItem('tc_error_logs', JSON.stringify(logs));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  // Performance monitoring
  startTimer(name: string): () => void {
    const start = performance.now();
    this.debug('PERF', `Timer started: ${name}`);
    
    return () => {
      const duration = performance.now() - start;
      this.info('PERF', `Timer ${name}: ${duration.toFixed(2)}ms`);
      return duration;
    };
  }

  // Component lifecycle logging
  componentMount(componentName: string) {
    this.debug('COMPONENT', `${componentName} mounted`);
  }

  componentUnmount(componentName: string) {
    this.debug('COMPONENT', `${componentName} unmounted`);
  }

  componentError(componentName: string, error: Error) {
    this.error('COMPONENT', `${componentName} error`, {
      message: error.message,
      stack: error.stack,
    });
  }
}

const logger = new Logger();
export default logger;